import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@rd-web-markets/shared/dist/util';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Col, Row, OverlayTrigger, Tooltip, Alert } from 'react-bootstrap';
import { AddButton, Button, MarketAddButton, MarketDeleteButton } from '@rd-web-markets/shared/dist/util/buttons';
import ImmutableUnderReviewFormControl from '@rd-web-markets/shared/dist/util/reviewEditControls/ImmutableUnderReviewFormControl';
import ProjectReportDateColumns from '@rd-web-markets/shared/dist/util/date/ProjectReportDateColumns';

import { PROJECT_REPORT_DATE_PICKER_TYPE,
        PROJECT_OVERVIEW_TABLE,
        PROJECT_GROUP_RPOGRAM_TYPE_NAME,
        SHOULD_SHOW_CREATE_PROJECTS_PAGE_FOR_CUSTOMER,
        SHOW_SEND_QUESTIONNAIRE_BUTTON_ON_PROJECT_OVERVIEW_PAGE,
        SHOW_IMPORT_PROJECT_AT_PROJECT_OVERVIEW,
        SHOW_BATCH_PROJECT_LOAD_BTN,
        SHOW_ADD_PROJECT_AT_PROJECT_OVERVIEW,
        SHOW_PROJECT_OVERVIEW_COST_SUMMARY_ROW,
        PROJECT_OVERVIEW_HEADER_ELEMENT_STYLES } from '@rd-web-markets/market/dist/constants';
import SetUpQuestionnaireModal from './SetUpQuestionnaireModal';
import CostFormatter from '@rd-web-markets/market/dist/claim_project_reports/claim_project_report_list/CostFormatter';
import { useGetUserClaimGroupAbilities } from '@rd-web-markets/market/dist/userAbilities';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import claimProjectReportService from '@services/claim_project_report.service';
import ProjectImportButtonAndModal from './ProjectImportButtonAndModal';
import projectImportService from '@rd-web-markets/shared/dist/services/claim_project_overview/project_import.service';
import { useParams, useHistory } from 'react-router-dom';
import StaffCostTemplateImportStatus from '@rd-web-markets/shared/dist/claim_group/StaffCostTemplateImportStatus';
import { ToggleVerticalChevronButton } from '@rd-web-markets/shared/dist/util/buttons/ToggleVerticalChevronButton';
import projectReportService from '@rd-web-markets/shared/dist/services/project_report/claim_project_report.service';
import createDefaultProjectAttributes from './createDefaultProjectAttributes';
import businessUnitsService from '@rd-web-markets/shared/dist/services/business_unit.service';
import subsidiaryCompanyService from '@rd-web-markets/shared/dist/services/subsidiary_company.service';
import { DatePicker, MonthPicker } from '@rd-web-markets/shared/dist/util/date';
import { createProjectOverviewHeaders } from './createProjectOverviewHeaders';
import AddProjectForm from './AddProjectForm';

const RD_STATUS_EDITABLE_PROJECT_STATUSES = new Set([
  'scoping_completed',
  'begun_tech_interview_scheduled',
  'begun_tech_interview_completed',
  'begun_in_manager_qa',
  'begun_manager_qa_completed',
  'finished',
]);

const isRdStatusEditable = (report) =>
  RD_STATUS_EDITABLE_PROJECT_STATUSES.has(report.completion_status);
/**
 * Currently this component renders the list of projects with headers as defined in the constant files for each market.
 * However in order to reuse the functionality for other pages, that more or less render the same things, we now have the
 * customHeaders and customColumns array properties.
 *
 * @param {*} param0
 * @returns
 */
const ClaimProjectReportsList = ({
  reports,
  setReports,
  claimGroup,
  setClaimGroup,
  handleToaster,
  params,
  setParams,
  getData,
  customHeaders = null,
  customColumns = null,
  groupProjects = false,
  renderExpandedRowContent = null,
  isTechnicalNarrativePage = false,
  totalCostSum = null,
  totalQeSum = null,
  tableColumnSelectorProps = null,
}) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const userClaimGroupAbilities = useGetUserClaimGroupAbilities({ user, claimGroup })
  const pageAccess = userClaimGroupAbilities?.pages?.ProjectOverviewPage
  const dispatch = useDispatch();
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [claimProjectReports, setClaimProjectReports] = useState(reports);
  const [newReport, setNewReport] = useState({});
  const datePickerType = PROJECT_REPORT_DATE_PICKER_TYPE
  const DatePickerComponent = datePickerType === 'month' ? MonthPicker : DatePicker
  const [modalShow, setModalShow] = useState(false);
  const { claimGroupId } = useParams();
  const { importStatus, importProjects } = projectImportService.useCrud(claimGroupId, getData);
  const history = useHistory();
  const [companyBusinessUnits, setCompanyBusinessUnits] = useState([]);
  const [subsidiaryCompanies, setSubsidiaryCompanies] = useState([]);
  const isDE = process.env.PUBLIC_URL === '/de';
  const isUK = process.env.PUBLIC_URL === '/uk';
  const [loading, setLoading] = useState(false);

  const useMultiColumnSorting = (!isDE && !isTechnicalNarrativePage)
  const useFilterSetup = (!isDE && !isTechnicalNarrativePage)
  tableColumnSelectorProps = (!isDE && !isTechnicalNarrativePage) ? tableColumnSelectorProps : null
  const isProgramProjectGrouping = groupProjects === 'program'
  const shouldShowGroupedProjectsToggle = groupProjects === true || isProgramProjectGrouping


  const shouldShowSubsidiarySelect =
    !isDE &&
    !!claimGroup?.company?.has_subsidiary_companies &&
    subsidiaryCompanies.length > 0;
  const shouldRemoveSubsidiaryName =
    isDE || !claimGroup?.company?.has_subsidiary_companies;
  const [,,fetchProject] = projectReportService.useGetById({
    claimGroupId: claimGroup.id,
    onSuccess: useCallback(updatedReport => {
      setReports(reports => {
        const index = reports.findIndex(r => r.id === updatedReport.id)
        reports.splice(index, 1, {
          ...reports[index],
          ...updatedReport
        })

        return [...reports]
      })
    }, [setReports])
  })
  const deleteProjectRequest = claimProjectReportService.useDelete({
    setLoading,
    handleToaster,
    onSuccess: () => window.location.reload(),
  });

  const fetchCompanyBusinessUnitsIfNeeded = useCallback(async () => {
    try {
      if (!claimGroup?.company?.business_unit_required) return;
      if (companyBusinessUnits.length > 0) return;

      const units = await businessUnitsService.all(claimGroup.company_id);
      setCompanyBusinessUnits(units || []);
    } catch (e) {
      dispatch(handleError(e));
    }
  }, [
    claimGroup?.company?.business_unit_required,
    claimGroup?.company_id,
    companyBusinessUnits.length,
    dispatch
  ]);

  const fetchSubsidiaryCompanies = useCallback(async () => {
    try {
      if (!isDE) {
        const subsidiaryCompanies = await subsidiaryCompanyService.all(claimGroup.company_id);
        setSubsidiaryCompanies(subsidiaryCompanies);
      }
    } catch (e) {
      dispatch(handleError(e));
    }
  }, [claimGroup?.company_id, dispatch, isDE]);

  useEffect(() => {
    const defaultProjectAttributes = createDefaultProjectAttributes(claimGroup)

    setNewReport(defaultProjectAttributes);
    setClaimProjectReports(reports);
  }, [showProjectForm, reports, claimGroup.from_date, claimGroup.to_date, claimGroup]);

  useEffect(() => {
    setClaimProjectReports(reports);
  }, [reports, claimGroup]);


  const headers = createProjectOverviewHeaders(customHeaders, claimGroup, groupProjects, t, shouldRemoveSubsidiaryName);

  const businessUnits = report => {
    return (report.project_business_units || [])?.map((pbu, index) => {
      const businessUnitName = pbu.business_unit.name
      const isLongName = businessUnitName.length > 50
      return (
        <OverlayTrigger placement='top' overlay={isLongName ? <Tooltip>{businessUnitName}</Tooltip> : <div/>}>
          <Badge key={index} bg="primary" className='me-2 mb-1'>
            {isLongName ? businessUnitName.substring(0, 50) + '...' : businessUnitName}
          </Badge>
        </OverlayTrigger>
      );
    });
  };

  const displayValue = value => {
    if (value === null || value === undefined || value === '') return ''
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'

    return value
  }

  const displayValues = values => {
    return values
      .filter(value => value !== null && value !== undefined && value !== '')
      .map(displayValue)
      .join(', ')
  }

  const competentProfessionalValues = (report, fieldName) => {
    return displayValues((report.competent_professional_claim_project_reports || []).map(competentProfessionalClaimProjectReport => {
      return competentProfessionalClaimProjectReport.competent_professional?.[fieldName]
    }))
  }

  const competentProfessionalReportValues = (report, fieldName) => {
    return displayValues((report.competent_professional_claim_project_reports || []).map(competentProfessionalClaimProjectReport => {
      return competentProfessionalClaimProjectReport[fieldName]
    }))
  }

  const customerContractValues = (report, fieldName) => {
    return displayValues((report.customer_contracts || []).map(customerContract => customerContract[fieldName]))
  }

  const requestDeletion = async (report) => {
    const text = user.account_type === 'customer' ? 'Are you sure you want to send deletion request' : 'Are you sure you want to cancel deletion request'
    if(window.confirm(text)) {
      report.requested_deletion = !report.requested_deletion;
      await claimProjectReportService.update(claimGroupId, report);
      window.location.reload();
    }
  }

  const deleteProject = (report) => {
    if (claimProjectReports.find(p => p.id == report.id)?.claim_project_reports?.length > 0) {
      return window.alert('Cannot delete - the program is associated with project(s)');
    }

    deleteProjectRequest({
      claimGroupId: claimGroup.id,
      reportId: report.id,
      confirmationButtonText: report.project_name
    });
  };

  const QuestionnairesButton = ({ report }) => {
    return (
      <Link
        className='text-center'
        to={`/${user.account_type}/claim_groups/${report.claim_group_id}/technical_proof/project_reports/${report.id}/questionnaires`}>
        <span className="material-icons text-center">assignment</span>
      </Link>
    );
  }

  const getProjectStatusOptions = (report, t) => {
    if (report.assessment === 'in_progress') {
      return [
        { value: 'unstarted', label: t('not_started') },
        { value: 'shortlisted_sampled', label: t('shortlisted_sampled') },
        { value: 'scoping_scheduled', label: t('scoping_scheduled') },
      ];
    } else if (report.assessment === 'completed') {
      return [
        { value: 'scoping_completed', label: t('scoping_completed') },
        { value: 'begun_tech_interview_scheduled', label: t('in_progress_tech_interview_scheduled') },
        { value: 'begun_tech_interview_completed', label: t('in_progress_tech_interview_completed') },
        { value: 'begun_in_manager_qa', label: t('in_progress_in_manager_qa') },
        { value: 'begun_manager_qa_completed', label: t('in_progress_manager_qa_completed') },
        { value: 'finished', label: t('completed') },
      ];
    } else {
      return [];
    }
  };

  const handleSubmit = async (report) => {
    try {
      await claimProjectReportService.update(claimGroupId, report);
    } catch (e) {
      dispatch(handleError(e));
    }
  };

  const handleChange = (event, report) => {
    const { name, value } = event.target;

    setClaimProjectReports((prev) =>
      prev.map((r) => {
        if (r.id !== report.id) return r;

        if (name === 'assessment') {
          return { ...r, assessment: value, completion_status: null };
        }

        return { ...r, [name]: value };
      })
    );
  };

  const mappingCompletionStatuses = {
    'unstarted': 'not_started',
    'begun_tech_interview_scheduled': 'in_progress_tech_interview_scheduled',
    'begun_tech_interview_completed': 'in_progress_tech_interview_completed',
    'begun_in_manager_qa': 'in_progress_in_manager_qa',
    'begun_manager_qa_completed': 'in_progress_manager_qa_completed',
    'finished': 'completed',
    'completion_status_unstarted': 'not_started',
    'completion_status_in_progress': 'in_progress',
    'completion_status_finished': 'completed',
    'shortlisted_sampled': 'project_shortlisted_sampled',
    'scoping_scheduled': 'project_scoping_scheduled',
    'scoping_completed': 'project_scoping_completed',
  }

  const renderExpandButton = (report) => {
    // In program grouping mode the top-level rows are already filtered to commercial program projects.
    // Do not rely on program_type here because the column selector controls whether that field is fetched.
    if (isProgramProjectGrouping && report.isNested) {
      return ''
    }

    if (groupProjects === true && report.program_type !== PROJECT_GROUP_RPOGRAM_TYPE_NAME) {
      return ''
    }

    const onExpandFn = () => {
      report.expanded = !report.expanded
      setClaimProjectReports([...reports])
    }

    return <ToggleVerticalChevronButton expanded={report.expanded} onClick={onExpandFn} />
  }

  const buildRow = (report) => {
    let columns = customColumns || [...PROJECT_OVERVIEW_TABLE.columns]

    if (shouldRemoveSubsidiaryName){
      columns = columns.filter(c => !['subsidiary_name', 'subsidiary_company_name'].includes(c))
    }

    if (user.account_type === 'customer') {
      columns = columns.filter(c => c !== 'deleteButton')
    } else if (isUK) {
      // if we are in the UK and the user is not a customer - make sure the delete button exists, and its always at the very end
      columns = columns.filter(c => c !== 'deleteButton')
      columns.push('deleteButton')
    }

    if (shouldShowGroupedProjectsToggle && !columns.includes('toggle_show_projects')) {
      columns.push('toggle_show_projects')
    }

    const requestDeletionButton = (report) => {
      if(report.requested_deletion) {
        if (user.account_type === 'customer') {
          return (
            <Button
              variant="success"
              size="md"
              disabled={true}
            >
              <span>Deletion Request Sent</span>
            </Button>
          );
        } else {
          return (
            <Button
              variant="success"
              size="md"
              onClick={() => requestDeletion(report)}
            >
              <span>Cancel Deletion Request</span>
            </Button>
          );
        }

      } else {
        if(user.account_type === 'customer') {
          return (
            <Button
              variant="danger"
              size="md"
              onClick={() => requestDeletion(report)}
            >
              <span>Request Deletion</span>
            </Button>
          );
        } else {
          return (
            <Button
              variant="danger"
              size="md"
              disabled={true}
            >
              <span>Cancel Deletion Request</span>
            </Button>
          );
        }
      }
    }

    const resetProject = async () => {
      await fetchProject(report.id)
    }

    columns = columns.map((c, columnIndex) => {
      switch(c) {
        case 'project_name':
          return pageAccess.canAccessProjectDetailsLink ?
            <Link to={`/${user.account_type}/claim_groups/${report.claim_group_id}/technical_proof/project_reports/${report.id}`}>{report.project_name}</Link>
            :
            report.project_name
        case 'subsidiary_name':
          return report?.subsidiary_company?.name || ''
        case 'subsidiary_company_name':
          return report?.subsidiary_company?.name || ''

        case 'assessment':
        case 'assessment_status':
          return (
            <ImmutableUnderReviewFormControl
              disabled={user.account_type === 'customer'}
              onBlur={() => handleSubmit(report)}
              as='select'
              name='assessment'
              onChange={(e) => handleChange(e, report)}
              size='md'
              value={report.assessment}
            >
              <option value='not_started'>{t('not_started')}</option>
              <option value='in_progress'>{t('in_progress')}</option>
              <option value='completed'>{t('completed')}</option>
            </ImmutableUnderReviewFormControl>
          )

        case 'project_code':
          return report.code
        case 'program_type':
          return report.program_type
        case 'cost_methodology_type':
          return report.cost_methodology_type
        case 'include_in_technical_report':
          return displayValue(report.include_in_technical_report)
        case 'are_rnd_trials_undertaken':
          return displayValue(report.are_rnd_trials_undertaken)
        case 'field_of_science_and_technology':
          return report.field_of_science_and_technology
        case 'created_by':
          return report.created_by
        case 'internal_project_number':
          return t(report.internal_project_number)
        case 'nature_of_project':
          return t(report.nature_of_project)
        case 'belspo_identification_number':
          return t(report.belspo_identification_number)
        case 'project_eligibility':
          return t(report.project_eligibility)
        case 'project_owner':
          return t('owner')
        case 'theme':
          return report.project_theme?.theme?.name || ''

        case 'category':
          return (
            <ImmutableUnderReviewFormControl
              disabled={!isRdStatusEditable(report) || user.account_type === 'customer'}
              as="select"
              name="category"
              onBlur={() => handleSubmit(report)}
              onChange={e => handleChange(e, report)}
              size="md"
              value={report.category || 'tbc'}
            >
              <option value='rnd'>{t('r&d')}</option>
              <option value='non_rnd'>{t('non_r&d')}</option>
              <option value='tbc'>{t('tbc')}</option>
            </ImmutableUnderReviewFormControl>
          )

        case 'bussiness_units':
          return businessUnits(report)
        case 'business_unit':
          return businessUnits(report)
        case 'total_cost':
          return (<CostFormatter
                    disabled={true}
                    object={report}
                    keyName={'total_cost'}
                  />)
        case 'total_qe':
            return (<CostFormatter
                      disabled={true}
                      object={report}
                      keyName={'total_qe'}
                    />)
        case 'cost':
          return report.total_cost ? t('completed') : t('in_progress')
        case 'completion_status_show_only':
            return t(mappingCompletionStatuses[report.completion_status])

        case 'completion_status': {
          const statusOptions = getProjectStatusOptions(report, t);
          return (
            <ImmutableUnderReviewFormControl
              as='select'
              onBlur={() => handleSubmit(report)}
              name='completion_status'
              disabled={report.assessment === 'not_started' || user.account_type === 'customer'}
              onChange={e => handleChange(e, report)}
              size='md'
              value={report.completion_status}
            >
              <option value={null}></option>
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </ImmutableUnderReviewFormControl>
          )
        }

        case 'documents':
          return (
            <Link
              className='text-center'
              to={`/${user.account_type}/claim_groups/${report.claim_group_id}/technical_proof/project_reports/${report.id}?key=documents&model_type=ClaimProjectReport&model_id=${report.id}&folder=`}>
              <span className="material-icons text-center">upload_file</span>
            </Link>
          )
        case 'toggle_show_projects':
          return renderExpandButton(report)
        case 'toggle_expand':
          return renderExpandButton(report)
        case 'request_deletion':
          return requestDeletionButton(report);
        case 'update_button_link':
          return (
            <Button variant="primary" size="md" as={Link} to={`/${user.account_type}/claim_groups/${report.claim_group_id}/technical_proof/project_reports/${report.id}?key=project_report_tab`}>
              <span>Update</span>
            </Button>
          );
        case 'start_date':
          return (
            <DatePickerComponent
              selected={report.start_date}
              disabled={true}
            />
          )
        case 'end_date':
          return (
            <DatePickerComponent
              selected={report.end_date}
              disabled={true}
            />
          )
        case 'rnd_start_date':
          return (
            <DatePickerComponent
              selected={report.rnd_start_date}
              disabled={true}
            />
          )
        case 'rnd_end_date':
          return (
            <DatePickerComponent
              selected={report.rnd_end_date}
              disabled={true}
            />
          )
        case 'milestone':
          return t(report.milestone)
        case 'rd_start_year':
          return report.rd_start_year
        case 'current_bsfz_response_date':
          return (
            <DatePicker
              selected={report.current_bsfz_response_date}
              disabled={true}
            />
          )
        case 'current_deadline_date':
          return (
            <DatePicker
              selected={report.current_deadline_date}
              disabled={true}
            />
          )
        case 'deleteButton':
          return (
            <MarketDeleteButton
              style={{ marginRight: '8px' }}
              onClick={() => deleteProject(report)}
            />
          )
        case 'escalation_required':
          return (
            report.escalation_required ? 'Yes' : 'No'
          )
        case 'commercial_project_name':
          return report.commercial_program_project?.project_name
        case 'commercial_project_code':
          return report.commercial_program_project?.code
        case 'commercial_project_cost_methodology_type':
          return report.commercial_program_project?.cost_methodology_type
        case 'cp_name':
          return competentProfessionalValues(report, 'name')
        case 'cp_qualifications':
          return competentProfessionalValues(report, 'qualifications')
        case 'cp_relevant_experience':
          return competentProfessionalValues(report, 'relevant_experience')
        case 'cp_email':
          return competentProfessionalValues(report, 'email')
        case 'cp_phone':
          return competentProfessionalValues(report, 'phone')
        case 'cp_exclude_from_rnd_report':
          return competentProfessionalReportValues(report, 'exclude_from_rnd_report')
        case 'cp_notes':
          return competentProfessionalReportValues(report, 'notes')
        case 'rnd_percentage':
          return report.rnd_percentage
        case 'total_rnd_hours':
          return report.total_rnd_hours
        case 'customer_name':
          return report.customer_name
        case 'customer_contract_details':
          return customerContractValues(report, 'details')
        case 'customer_contract_upstream_contract':
          return customerContractValues(report, 'upstream_contract')
        case 'customer_contract_upstream_review':
          return customerContractValues(report, 'upstream_review')
        case 'questionnaires':
          return <QuestionnairesButton report={report} />
        case 'rd_baseline':
          return displayValue(report.rd_baseline)
        case 'rd_advance':
          return displayValue(report.rd_advance)
        case 'rd_uncertainty':
          return displayValue(report.rd_uncertainty)
        case 'rd_work_done':
          return displayValue(report.rd_work_done)
        case 'rd_additional_comments':
          return displayValue(report.rd_additional_comments)
        case 'technical_narrative_current_state_of_knowledge':
        case 'tn_knowledge':
          return displayValue(report.technical_narrative_current_state_of_knowledge)
        case 'scientific_or_technological_advances_sought':
        case 'tn_advances':
          return displayValue(report.scientific_or_technological_advances_sought)
        case 'technical_narrative_technical_uncertainties':
        case 'tn_uncertainties':
          return displayValue(report.technical_narrative_technical_uncertainties)
        case 'technical_narrative_uncertainties_overcome':
        case 'tn_overcome':
          return displayValue(report.technical_narrative_uncertainties_overcome)
        default:
          if (customColumns.length) {
            if (typeof customColumns[columnIndex] === 'function') {
              return customColumns[columnIndex](report, resetProject)
            } else {
              return customColumns[columnIndex]
            }
          } else {
            console.log('Heading doesnt exist')
            return undefined
          }
      }
    });

    return {
      key: report.id,
      data: { project: report, resetProject },
      columns: columns,
      className: report.isNested ? 'nested-table-row' : '',
    }
  }

  const reportsForRows = []
  claimProjectReports.forEach(cpr => {
    reportsForRows.push(cpr)

    // This is very old row expanding functionality that needs to be removed.
    // Therefore if renderExpandedRowContent is provided we dont use it.
    if (!renderExpandedRowContent && cpr.expanded) {
      cpr.claim_project_reports.forEach(nestedCpr => {
        reportsForRows.push({ ...nestedCpr, isNested: true })
      })
    }
  })

  const summaryRow = (() => {
    const totalCostIndex = headers.findIndex(h => h.orderBy === 'total_cost')
    const totalQeIndex = headers.findIndex(h => h.orderBy === 'total_qe')
    const emptyCols = headers.map((_, i) => {
      if (i === totalCostIndex) return <CostFormatter disabled={true} object={{ total_cost: totalCostSum }} keyName='total_cost' />
      if (i === totalQeIndex) return <CostFormatter disabled={true} object={{ total_qe: totalQeSum }} keyName='total_qe' />
      return ''
    })

    return {
      key: 'summary-row',
      columns: emptyCols,
      className: 'fw-bold table-info'
    }
  })()
  const shouldShowCostSummaryRow = SHOW_PROJECT_OVERVIEW_COST_SUMMARY_ROW &&
    headers.some(h => h.orderBy === 'total_cost') &&
    headers.some(h => h.orderBy === 'total_qe')

  const rows =
  [
    ...(shouldShowCostSummaryRow ? [summaryRow] : []),
    ...reportsForRows?.map(report => {
      const row = buildRow(report)
      if (renderExpandedRowContent) {
        row.expandable = true
        row.expanded = !!report.expanded
        row.renderExpandedContent = renderExpandedRowContent
      }

    return row
  })];

  const addProject = async () => {
    await fetchCompanyBusinessUnitsIfNeeded();
    await fetchSubsidiaryCompanies();
    setShowProjectForm(true);
  };

  const orderParams = (eventKey, direction) => {
    return `${headers[eventKey].orderBy} ${direction}`
  };

  const sortreports = (sortColumns) => {
    const orderBy = sortColumns
      .map(({ headerIndex, direction }) => `${headers[headerIndex].orderBy} ${direction}`)
      .join(', ')

    setParams({...params, page: 1, order_by: orderBy})
  }

  const sortReportsByHeader = (eventKey, direction) => {
    setParams({...params, page: 1, order_by: orderParams(eventKey, direction)})
  }

  const addBatchLoad = () => {
    history.replace(`/${user.account_type}/claim_groups/${claimGroup.id}/data_centre/main_page`);
  }

  const headerButtons = () => {
    if((user.account_type === 'admin' || user.account_type === 'consultant' || user.account_type === 'manager' || user.account_type === 'super_consultant') && !isTechnicalNarrativePage) {
      return (
        <>
          { SHOW_SEND_QUESTIONNAIRE_BUTTON_ON_PROJECT_OVERVIEW_PAGE &&
            <Button onClick={() => setModalShow(true)} className='me-2'>
              {t('send_questionnaire')}
            </Button>
          }

          { SHOW_IMPORT_PROJECT_AT_PROJECT_OVERVIEW && (
            <ProjectImportButtonAndModal importProjects={importProjects} importStatus={importStatus}/>
          )}
          { SHOW_ADD_PROJECT_AT_PROJECT_OVERVIEW &&
            <MarketAddButton
              isImmutableUnderReview={true}
              disabled={false}
              onClick={() => addProject()}
              style={{ marginRight: '10px' }}
              text={t('add_project')}
            />
          }
          {SHOW_BATCH_PROJECT_LOAD_BTN && (
            <AddButton
              variant="info"
              className="ml-3"
              isImmutableUnderReview={true}
              disabled={false}
              onClick={() => addBatchLoad()}
              text={t('batch_load_multiple_projects')}
            />
          )}
      </>
      )
    } else if(user.account_type === 'customer' && SHOULD_SHOW_CREATE_PROJECTS_PAGE_FOR_CUSTOMER) {
      return (
        <>
          <MarketAddButton
            isImmutableUnderReview={true}
            disabled={false}
            onClick={() => addProject()}
            text={t('add_project')}
          />
        </>
      )
    } else {
      return <></>
    }
  }

  const headerButtonsList = useMemo(() => {
    let buttons = []
    if ((user.account_type === 'admin' || user.account_type === 'consultant' || user.account_type === 'manager' || user.account_type === 'super_consultant') && !isTechnicalNarrativePage) {
      buttons = [
        SHOW_SEND_QUESTIONNAIRE_BUTTON_ON_PROJECT_OVERVIEW_PAGE ?
          <Button onClick={() => setModalShow(true)} className='me-2'>
            {t('send_questionnaire')}
          </Button>
          :
          null,
        SHOW_IMPORT_PROJECT_AT_PROJECT_OVERVIEW ?
          <ProjectImportButtonAndModal importProjects={importProjects} importStatus={importStatus}/>
          :
          null,
        SHOW_ADD_PROJECT_AT_PROJECT_OVERVIEW ?
          <MarketAddButton
            isImmutableUnderReview={true}
            disabled={false}
            onClick={() => addProject()}
            style={{ marginRight: '10px' }}
            text={t('add_project')}
          />
          :
          null,
        SHOW_BATCH_PROJECT_LOAD_BTN ?
          <AddButton
            variant="info"
            className="ml-3"
            isImmutableUnderReview={true}
            disabled={false}
            onClick={() => addBatchLoad()}
            text={t('batch_load_multiple_projects')}
          />
          :
          null,

      ]
    } else if(user.account_type === 'customer' && SHOULD_SHOW_CREATE_PROJECTS_PAGE_FOR_CUSTOMER) {
      buttons = [
        <MarketAddButton
          isImmutableUnderReview={true}
          disabled={false}
          onClick={() => addProject()}
          text={t('add_project')}
        />
      ]
    } else {
      buttons = []
    }

    return buttons.filter(Boolean)
  }, [addBatchLoad, addProject, importProjects, importStatus, isTechnicalNarrativePage, t, user.account_type])

  return (
    <div>
      <SetUpQuestionnaireModal
        show={modalShow}
        title={t('questionnaire_set_up')}
        onHide={() => setModalShow(false)}
        submitText={t('send')}
        user={user}
        claimProjectReports={claimProjectReports}
        claimGroup={claimGroup}
      />

      { isDE &&
        <Row>
          <Col className={`${window.MARKET_CONSTANTS.SHOW_ADD_PROJECT_LEFT ? 'd-flex' : ''} text-end`}>
            {headerButtons()}
          </Col>
        </Row>
      }

      { SHOW_IMPORT_PROJECT_AT_PROJECT_OVERVIEW && (
        <Row className='my-3'>
          <Col>
            <StaffCostTemplateImportStatus importStatus={importStatus} />
          </Col>
        </Row>
      )}

      { claimGroup.active_change_set &&
        <Alert variant='info'>
          Claim group under review
        </Alert>
      }

      <AddProjectForm
        showProjectForm={showProjectForm}
        setShowProjectForm={setShowProjectForm}
        newReport={newReport}
        setNewReport={setNewReport}
        claimGroup={claimGroup}
        setClaimGroup={setClaimGroup}
        handleToaster={handleToaster}
        datePickerType={datePickerType}
        shouldShowSubsidiarySelect={shouldShowSubsidiarySelect}
        subsidiaryCompanies={subsidiaryCompanies}
        getData={getData}
        params={params}
      />

      <div className='project-overview-table-wrapper mt-5'>
        <Table
          headers={headers}
          rows={rows}
          onSort={useMultiColumnSorting ? sortreports : sortReportsByHeader}
          useMultiColumnSorting={useMultiColumnSorting}
          useFilterSetup={useFilterSetup}
          onFilter={(tableHeaders, queryString) => {
            setParams({
              ...params,
              page: 1,
              query: queryString
            })
          }}
          textFilterQueryStringParamName='query'
          headerTopElementStyle={PROJECT_OVERVIEW_HEADER_ELEMENT_STYLES.TOP_ELEMENT}
          headerLowerItemStyle={PROJECT_OVERVIEW_HEADER_ELEMENT_STYLES.LOWER_ELEMENT}
          showControls={false}
          tableColumnSelectorProps={tableColumnSelectorProps}
          additionalTableControlButtons={headerButtonsList}
        />
      </div>
    </div>
  );
}

export default ClaimProjectReportsList
